import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, ArrowRight, X, ExternalLink } from "lucide-react";
import { DIVISIONS, CATEGORIES, enrichSubsidiary } from "@/data/divisions";
import { SUB } from "@/constants/testIds";
import { useSubsidiaries } from "@/hooks/useSubsidiaries";

export default function Subsidiaries() {
  const [params, setParams] = useSearchParams();
  const initialDivision = params.get("division") || "all";
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [divisionId, setDivisionId] = useState(initialDivision);

  const { subs, loading } = useSubsidiaries();
  const enriched = useMemo(() => subs.map(enrichSubsidiary), [subs]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (divisionId !== "all") p.set("division", divisionId);
    setParams(p, { replace: true });
  }, [divisionId, setParams]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return enriched.filter((s) => {
      if (divisionId !== "all" && s.division_id !== divisionId) return false;
      if (category !== "All" && s.category !== category) return false;
      if (!q) return true;
      return (
        s.name.toLowerCase().includes(q) ||
        s.service.toLowerCase().includes(q) ||
        s.blurb.toLowerCase().includes(q) ||
        (s.division_name || "").toLowerCase().includes(q)
      );
    });
  }, [enriched, query, category, divisionId]);

  return (
    <div className="text-white">
      {/* HERO */}
      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-14">
          <div className="eyebrow mb-6">The Group Entity Registry</div>
          <h1 className="font-display font-black text-5xl md:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
            {enriched.length || "…"} micro-subsidiaries.<br />
            <span className="text-[#FF5722]">One master switchboard.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
            Search, filter and browse every registered entity in Billtech Holdings. Each card
            represents an independent legal subsidiary carrying its own trade name, technical
            specialty and operational leadership. Click any name with a live link to visit that
            subsidiary&rsquo;s own website.
          </p>
        </div>
      </section>

      {/* CONTROLS */}
      <section className="sticky top-16 lg:top-20 z-30 backdrop-blur-xl bg-[#060A14]/85 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1 max-w-xl">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              placeholder="Search entities, services, or divisions..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid={SUB.searchInput}
              className="w-full bg-[#0C1322] border border-white/10 pl-10 pr-10 py-3 text-sm text-white placeholder:text-white/40 focus:border-[#FF5722] focus:ring-1 focus:ring-[#FF5722] outline-none"
            />
            {query && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                data-testid="subsidiary-search-clear"
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={SUB.categoryFilter(c)}
                className={`px-3 py-2 text-[11px] font-mono uppercase tracking-[0.2em] border transition-colors ${
                  category === c
                    ? "bg-[#FF5722] border-[#FF5722] text-white"
                    : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-3 flex flex-wrap gap-2 items-center">
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/40 mr-2">Division:</span>
          <FilterChip active={divisionId === "all"} onClick={() => setDivisionId("all")} testId="division-chip-all">
            All 08
          </FilterChip>
          {DIVISIONS.map((d) => (
            <FilterChip
              key={d.id}
              active={divisionId === d.id}
              onClick={() => setDivisionId(d.id)}
              testId={`division-chip-${d.id}`}
            >
              {d.code} · {d.name.replace("Billtech ", "").split("&")[0].trim()}
            </FilterChip>
          ))}
        </div>
      </section>

      {/* RESULTS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">
            {loading ? "Loading..." : `${filtered.length} of ${enriched.length} entities`}
          </div>
          {(query || category !== "All" || divisionId !== "all") && (
            <button
              onClick={() => {
                setQuery("");
                setCategory("All");
                setDivisionId("all");
              }}
              className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#FF5722] hover:underline"
              data-testid="subsidiary-reset-filters"
            >
              Reset filters ×
            </button>
          )}
        </div>

        {!loading && filtered.length === 0 ? (
          <div className="border border-white/10 p-16 text-center">
            <div className="font-display text-2xl mb-2">No entities match.</div>
            <div className="text-white/60 text-sm">Try adjusting your search or filter selection.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map((s) => (
              <SubsidiaryCard key={s.slug} s={s} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] border transition-colors ${
        active
          ? "bg-white text-[#060A14] border-white"
          : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function SubsidiaryCard({ s }) {
  const hasLink = !!s.website_url;
  const NameTag = hasLink ? "a" : "div";
  const nameProps = hasLink
    ? {
        href: s.website_url,
        target: "_blank",
        rel: "noopener noreferrer",
        className: "inline-flex items-center gap-1.5 hover:text-[#FF5722] transition-colors",
      }
    : { className: "" };

  return (
    <div
      data-testid={SUB.card(s.slug)}
      className="border border-white/10 bg-[#0C1322] p-5 flex flex-col hover:border-[#FF5722] transition-colors group"
    >
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#FF5722] mb-2">
        A Billtech Holdings Company · Div {s.division_code}
      </div>
      <div className="font-display font-bold text-white text-[15px] leading-snug mb-2">
        <NameTag {...nameProps} data-testid={hasLink ? `subsidiary-link-${s.id}` : undefined}>
          {s.name}
          {hasLink && <ExternalLink size={12} className="opacity-70" />}
        </NameTag>
      </div>
      <div className="text-xs text-white/60 mb-3 flex items-center gap-2">
        <span className="inline-block w-1.5 h-1.5 bg-[#00B0FF]" />
        <span className="text-white/85 font-medium">{s.service}</span>
      </div>
      <div className="text-xs text-[#94A3B8] leading-relaxed flex-1">{s.blurb}</div>
      <Link
        to={`/contact?division=${s.division_id}&entity=${encodeURIComponent(s.name)}`}
        data-testid={SUB.requestBtn(s.slug)}
        className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-white/70 hover:text-[#FF5722]"
      >
        Request Service <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  );
}
